import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  BarChart3,
  PieChart,
  Activity,
  Calendar,
  Filter
} from 'lucide-react';
import { Workflow, WorkflowExecution, AnalyticsPeriod } from '../types/workflow';
import { workflowService } from '../services/workflowService';

interface WorkflowAnalyticsProps {
  workflows: Workflow[];
  executions: WorkflowExecution[];
}

export const WorkflowAnalytics: React.FC<WorkflowAnalyticsProps> = ({
  workflows,
  executions
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<AnalyticsPeriod>('7d');
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>('all');
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [selectedPeriod, selectedWorkflow]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      if (selectedWorkflow === 'all') {
        const stats = await workflowService.getStats();
        setAnalytics(stats);
      } else {
        const workflowAnalytics = await workflowService.getAnalytics(selectedWorkflow, selectedPeriod);
        setAnalytics(workflowAnalytics);
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate metrics from executions
  const calculateMetrics = () => {
    const filteredExecutions = selectedWorkflow === 'all' 
      ? executions 
      : executions.filter(e => e.workflowId === selectedWorkflow);

    const total = filteredExecutions.length;
    const successful = filteredExecutions.filter(e => e.status === 'completed').length;
    const failed = filteredExecutions.filter(e => e.status === 'failed').length;
    const running = filteredExecutions.filter(e => e.status === 'running').length;
    const avgDuration = filteredExecutions
      .filter(e => e.duration)
      .reduce((sum, e) => sum + (e.duration || 0), 0) / filteredExecutions.filter(e => e.duration).length || 0;

    return {
      total,
      successful,
      failed,
      running,
      successRate: total > 0 ? (successful / total) * 100 : 0,
      avgDuration: Math.round(avgDuration / 1000) // Convert to seconds
    };
  };

  const metrics = calculateMetrics();

  // Group executions by date for trend chart
  const getExecutionTrends = () => {
    const trends: Record<string, { date: string; executions: number; successful: number; failed: number }> = {};
    
    executions.forEach(execution => {
      const date = new Date(execution.startedAt).toISOString().split('T')[0];
      if (!trends[date]) {
        trends[date] = { date, executions: 0, successful: 0, failed: 0 };
      }
      trends[date].executions++;
      if (execution.status === 'completed') trends[date].successful++;
      if (execution.status === 'failed') trends[date].failed++;
    });

    return Object.values(trends).sort((a, b) => a.date.localeCompare(b.date));
  };

  const trendData = getExecutionTrends();

  // Get top performing workflows
  const getTopWorkflows = () => {
    const workflowStats: Record<string, { name: string; executions: number; successRate: number }> = {};
    
    executions.forEach(execution => {
      const workflow = workflows.find(w => w.id === execution.workflowId);
      if (!workflow) return;
      
      if (!workflowStats[execution.workflowId]) {
        workflowStats[execution.workflowId] = {
          name: workflow.name,
          executions: 0,
          successRate: 0
        };
      }
      workflowStats[execution.workflowId].executions++;
    });

    // Calculate success rates
    Object.keys(workflowStats).forEach(workflowId => {
      const workflowExecutions = executions.filter(e => e.workflowId === workflowId);
      const successful = workflowExecutions.filter(e => e.status === 'completed').length;
      workflowStats[workflowId].successRate = workflowExecutions.length > 0 
        ? (successful / workflowExecutions.length) * 100 
        : 0;
    });

    return Object.values(workflowStats)
      .sort((a, b) => b.executions - a.executions)
      .slice(0, 5);
  };

  const topWorkflows = getTopWorkflows();

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Analytics</h2>
          <p className="text-neutral-400">Workflow performance insights</p>
        </div>
        
        <div className="flex items-center gap-4">
          <select
            value={selectedWorkflow}
            onChange={(e) => setSelectedWorkflow(e.target.value)}
            className="px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Workflows</option>
            {workflows.map(workflow => (
              <option key={workflow.id} value={workflow.id}>
                {workflow.name}
              </option>
            ))}
          </select>
          
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as AnalyticsPeriod)}
            className="px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Total Executions</p>
              <p className="text-3xl font-bold text-white">{metrics.total}</p>
              <div className="flex items-center gap-1 mt-2">
                <TrendingUp className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-sm">+12% vs last period</span>
              </div>
            </div>
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <Activity className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Success Rate</p>
              <p className="text-3xl font-bold text-white">{metrics.successRate.toFixed(1)}%</p>
              <div className="flex items-center gap-1 mt-2">
                <TrendingUp className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-sm">+5% vs last period</span>
              </div>
            </div>
            <div className="p-3 bg-green-500/20 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Avg Duration</p>
              <p className="text-3xl font-bold text-white">{metrics.avgDuration}s</p>
              <div className="flex items-center gap-1 mt-2">
                <TrendingDown className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-sm">-8% vs last period</span>
              </div>
            </div>
            <div className="p-3 bg-purple-500/20 rounded-lg">
              <Clock className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </div>

        <div className="bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Failed Executions</p>
              <p className="text-3xl font-bold text-white">{metrics.failed}</p>
              <div className="flex items-center gap-1 mt-2">
                <TrendingDown className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-sm">-15% vs last period</span>
              </div>
            </div>
            <div className="p-3 bg-red-500/20 rounded-lg">
              <AlertCircle className="w-6 h-6 text-red-400" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Execution Trends Chart */}
        <div className="bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Execution Trends
            </h3>
          </div>
          
          <div className="h-64 flex items-end justify-between gap-2">
            {trendData.slice(-7).map((day, index) => {
              const maxExecutions = Math.max(...trendData.map(d => d.executions));
              const height = maxExecutions > 0 ? (day.executions / maxExecutions) * 100 : 0;
              
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center">
                  <div className="w-full flex flex-col justify-end h-48">
                    <div 
                      className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t"
                      style={{ height: `${height}%` }}
                    />
                  </div>
                  <div className="mt-2 text-xs text-neutral-400">
                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                  <div className="text-xs text-white font-medium">
                    {day.executions}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Performing Workflows */}
        <div className="bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Top Performing Workflows
            </h3>
          </div>
          
          <div className="space-y-4">
            {topWorkflows.map((workflow, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-neutral-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-blue-400 font-bold text-sm">#{index + 1}</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">{workflow.name}</p>
                    <p className="text-neutral-400 text-sm">{workflow.executions} executions</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white font-medium">{workflow.successRate.toFixed(1)}%</p>
                  <p className="text-neutral-400 text-sm">success rate</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Execution Status Distribution */}
      <div className="bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <PieChart className="w-5 h-5" />
            Execution Status Distribution
          </h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-3 relative">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle
                  cx="40"
                  cy="40"
                  r="30"
                  stroke="#374151"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="40"
                  cy="40"
                  r="30"
                  stroke="#10b981"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${(metrics.successful / metrics.total) * 188.4} 188.4`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white font-bold">{metrics.successful}</span>
              </div>
            </div>
            <p className="text-green-400 font-medium">Successful</p>
            <p className="text-neutral-400 text-sm">
              {metrics.total > 0 ? ((metrics.successful / metrics.total) * 100).toFixed(1) : 0}%
            </p>
          </div>

          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-3 relative">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle
                  cx="40"
                  cy="40"
                  r="30"
                  stroke="#374151"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="40"
                  cy="40"
                  r="30"
                  stroke="#ef4444"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${(metrics.failed / metrics.total) * 188.4} 188.4`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white font-bold">{metrics.failed}</span>
              </div>
            </div>
            <p className="text-red-400 font-medium">Failed</p>
            <p className="text-neutral-400 text-sm">
              {metrics.total > 0 ? ((metrics.failed / metrics.total) * 100).toFixed(1) : 0}%
            </p>
          </div>

          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-3 relative">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle
                  cx="40"
                  cy="40"
                  r="30"
                  stroke="#374151"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="40"
                  cy="40"
                  r="30"
                  stroke="#3b82f6"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${(metrics.running / metrics.total) * 188.4} 188.4`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white font-bold">{metrics.running}</span>
              </div>
            </div>
            <p className="text-blue-400 font-medium">Running</p>
            <p className="text-neutral-400 text-sm">
              {metrics.total > 0 ? ((metrics.running / metrics.total) * 100).toFixed(1) : 0}%
            </p>
          </div>

          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-3 relative">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle
                  cx="40"
                  cy="40"
                  r="30"
                  stroke="#374151"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="40"
                  cy="40"
                  r="30"
                  stroke="#6b7280"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${((metrics.total - metrics.successful - metrics.failed - metrics.running) / metrics.total) * 188.4} 188.4`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white font-bold">
                  {metrics.total - metrics.successful - metrics.failed - metrics.running}
                </span>
              </div>
            </div>
            <p className="text-neutral-400 font-medium">Other</p>
            <p className="text-neutral-400 text-sm">
              {metrics.total > 0 ? (((metrics.total - metrics.successful - metrics.failed - metrics.running) / metrics.total) * 100).toFixed(1) : 0}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 